import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useCampaignContentBlocks, useCampaignMutations } from '../../../../hooks/campaign-hooks';
import {
  Campaign,
  CampaignContentBlock,
  CampaignContentBlockPayload,
} from '../../../../support/campaigns';
import { GhostButton, SectionHeader, builderStyles } from './primitives';
import { CATEGORY_SCHEMAS } from './contentBlockSchemas';
import { SchemaPayloadFields } from './SchemaPayloadFields';

/**
 * Admin editor for a campaign's generic, category-keyed content blocks (`CampaignContentBlock`) — the
 * DB-authoritative replacement for the runner's bundled `guided/config/*` lists.
 *
 * The admin picks (or enters) a `category`, then edits the blocks in it ordered by `order`: each block
 * has a `label`, a stable `key`, an `order`, a `hidden` toggle, an optional self-nesting `parent`
 * (chosen from the other blocks in this campaign, so hierarchy is authorable), and a category-shaped
 * `payload`. The generic payload editor is a validated JSON textarea; the one exception is the opt-in
 * `ai_question_config` category, which gets a first-class, friendly sub-editor (see
 * `AiQuestionConfigEditor`) so AI question suggestion is an explicit, off-by-default admin switch.
 *
 * Persistence is the full-record PUT the rest of the builder uses (`{ ...row, ...changes }`).
 */

export const AI_QUESTION_CONFIG_CATEGORY = 'ai_question_config';

const AI_QUESTION_CONFIG_DEFAULT: CampaignContentBlockPayload = {
  enabled: false,
  pool_category: '',
  suggest_count: 3,
  refinement_context: '',
};

const byOrder = <T extends { order?: number; id: number }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id);

const nextOrderOf = (rows: Array<{ order?: number }>): number =>
  rows.length ? Math.max(...rows.map((r) => r.order ?? 0)) + 1 : 0;

export function ContentBlocksEditor(props: {
  campaign: Campaign;
  canWrite: boolean;
  /**
   * Optional category allowlist. When set, only these categories are exposed (in this order), the
   * free-text "new category" creator is hidden, and an allowed category with no blocks yet still
   * appears so the first block can be added. Used to embed a focused slice of the corpus — e.g. the
   * deep-dive scenario content — inside a step editor while reusing this one CRUD surface.
   */
  only?: string[];
  /** Friendly display labels for the category dropdown, keyed by raw category. */
  categoryLabels?: Record<string, string>;
  /** Override the panel header (defaults to the generic "Content blocks"). */
  title?: string;
  subtitle?: string;
}): JSX.Element {
  const { campaign, canWrite, only, categoryLabels, title, subtitle } = props;
  const scoped = !!only && only.length > 0;

  // Fetch every block for the campaign (no category filter) so we can offer the category list, the
  // parent picker across the campaign, and the pool-category picker for ai_question_config.
  const { data: allRows = [], isLoading } = useCampaignContentBlocks(campaign.id);
  const mut = useCampaignMutations('contentBlock');

  const categories = React.useMemo(
    () =>
      scoped
        ? // Scoped: exactly the allowlist, in the given order, whether or not blocks exist yet.
          (only as string[])
        : // Unscoped: union of existing categories + all known schema categories, so a category with
          // zero blocks can still be discovered and created.
          Array.from(
            new Set([
              ...allRows.map((b) => b.category).filter(Boolean),
              ...Object.keys(CATEGORY_SCHEMAS),
            ]),
          ).sort(),
    [allRows, scoped, only],
  );
  const labelFor = (c: string) => categoryLabels?.[c] ?? c;

  const [category, setCategory] = React.useState<string>('');
  const [newCategory, setNewCategory] = React.useState('');

  // Default the active category to the first that exists once rows load.
  React.useEffect(() => {
    if (!category && categories.length) setCategory(categories[0]);
  }, [categories, category]);

  const activeRows = React.useMemo(
    () => byOrder(allRows.filter((b) => b.category === category)),
    [allRows, category],
  );

  const isAiConfig = category === AI_QUESTION_CONFIG_CATEGORY;

  // Blocks are collapsed by default (a dense one-line summary); expanding one reveals its edit form.
  const [expanded, setExpanded] = React.useState<Set<number>>(() => new Set());
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = (row: CampaignContentBlock, next: Partial<CampaignContentBlock>) => {
    mut.update.mutate({ ...row, ...next, id: row.id });
  };
  const savePayload = (row: CampaignContentBlock, payload: CampaignContentBlockPayload) =>
    save(row, { payload });

  const add = () => {
    if (!category) return;
    mut.create.mutate(
      {
        campaign: campaign.id,
        parent: null,
        category,
        key: '',
        label: '',
        order: nextOrderOf(activeRows),
        hidden: false,
        payload: isAiConfig ? { ...AI_QUESTION_CONFIG_DEFAULT } : {},
        metadata: {},
      },
      {
        // Auto-expand the freshly created block so it's ready to edit.
        onSuccess: (created: CampaignContentBlock) => {
          if (created && typeof created.id === 'number') {
            setExpanded((prev) => new Set(prev).add(created.id));
          }
        },
      },
    );
  };

  const applyNewCategory = () => {
    const c = newCategory.trim();
    if (!c) return;
    setCategory(c);
    setNewCategory('');
  };

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title={title ?? 'Content blocks'}
        subtitle={
          subtitle ??
          'The long-tail content lists (discovery/guide/scenario/agenda…) this campaign is built from, one category at a time. Pick or create a category, then order and edit its blocks.'
        }
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <Stack spacing={2}>
          {/* --- Category picker --- */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField
              select
              label="Category"
              size="small"
              value={categories.includes(category) ? category : ''}
              onChange={(e) => setCategory(e.target.value)}
              sx={{ minWidth: 240 }}
              helperText="The block family to edit"
            >
              {categories.length === 0 && (
                <MenuItem value="" disabled>
                  No categories yet
                </MenuItem>
              )}
              {categories.map((c) => (
                <MenuItem key={c} value={c}>
                  {labelFor(c)}
                </MenuItem>
              ))}
            </TextField>
            {canWrite && !scoped && (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  label="New category"
                  size="small"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyNewCategory()}
                  placeholder="e.g. guide_question"
                  sx={{ minWidth: 200 }}
                />
                <GhostButton onClick={applyNewCategory} disabled={!newCategory.trim()}>
                  Use category
                </GhostButton>
              </Stack>
            )}
          </Stack>

          {!category && (
            <Typography variant="body2" color="text.secondary">
              Pick a category above, or create one, to start authoring blocks.
            </Typography>
          )}

          {category && (
            <>
              {isAiConfig && (
                <Alert severity="info">
                  This category controls the opt-in AI question suggestion. It is off by default — when a
                  block&apos;s toggle is off, only the predefined question bank shows to SEs.
                </Alert>
              )}

              {activeRows.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No blocks in <strong>{labelFor(category)}</strong> yet.
                </Typography>
              )}

              <Stack spacing={1.5}>
                {activeRows.map((row, index) => (
                  <BlockCard
                    key={row.id}
                    row={row}
                    index={index}
                    total={activeRows.length}
                    siblings={allRows}
                    categories={categories}
                    campaignId={campaign.id}
                    isAiConfig={isAiConfig}
                    canWrite={canWrite}
                    isSaving={mut.isSaving}
                    expanded={expanded.has(row.id)}
                    onToggle={() => toggle(row.id)}
                    onSave={save}
                    onSavePayload={savePayload}
                    onMove={(dir) => {
                      const b = activeRows[index + dir];
                      if (!b) return;
                      save(row, { order: b.order });
                      save(b, { order: row.order });
                    }}
                    onDelete={() => mut.remove.mutate(row.id)}
                  />
                ))}
              </Stack>

              {canWrite && (
                <Box>
                  <GhostButton onClick={add} disabled={mut.isSaving}>
                    + Add block to {labelFor(category)}
                  </GhostButton>
                </Box>
              )}
            </>
          )}
        </Stack>
      )}
    </div>
  );
}

/** One content block's editable card — identity/order/parent plus the payload sub-editor. */
function BlockCard(props: {
  row: CampaignContentBlock;
  index: number;
  total: number;
  siblings: CampaignContentBlock[];
  categories: string[];
  campaignId: number;
  isAiConfig: boolean;
  canWrite: boolean;
  isSaving: boolean;
  expanded: boolean;
  onToggle: () => void;
  onSave: (row: CampaignContentBlock, next: Partial<CampaignContentBlock>) => void;
  onSavePayload: (row: CampaignContentBlock, payload: CampaignContentBlockPayload) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}): JSX.Element {
  const {
    row,
    index,
    total,
    siblings,
    categories,
    campaignId,
    isAiConfig,
    canWrite,
    isSaving,
    expanded,
    onToggle,
    onSave,
    onSavePayload,
    onMove,
    onDelete,
  } = props;

  // Parent candidates: other blocks in the same campaign (never self), so hierarchy is authorable.
  const parentOptions = siblings.filter((b) => b.id !== row.id);

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={onToggle}
      sx={{
        borderRadius: 1,
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: 'none',
        opacity: row.hidden ? 0.55 : 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.5, minWidth: 0 } }}
      >
        <span className={builderStyles.tag}>#{row.order}</span>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600, minWidth: 0, flexShrink: 1 }}>
          {row.label || row.key || '(untitled block)'}
        </Typography>
        {row.key && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: 'monospace' }}>
            {row.key}
          </Typography>
        )}
        {row.hidden && (
          <Chip size="small" variant="outlined" icon={<VisibilityOffIcon fontSize="small" />} label="Hidden" />
        )}
        {canWrite && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto', pl: 1 }}>
            <GhostButton
              onClick={(e) => {
                e.stopPropagation();
                onMove(-1);
              }}
              disabled={index === 0 || isSaving}
            >
              ↑
            </GhostButton>
            <GhostButton
              onClick={(e) => {
                e.stopPropagation();
                onMove(1);
              }}
              disabled={index === total - 1 || isSaving}
            >
              ↓
            </GhostButton>
            <Tooltip title={row.hidden ? 'Hidden — show' : 'Hide from consumers'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onSave(row, { hidden: !row.hidden });
                }}
              >
                {row.hidden ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={isSaving}
              aria-label="Remove block"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </AccordionSummary>
      <AccordionDetails>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
        <TextField
          label="Label"
          size="small"
          fullWidth
          defaultValue={row.label}
          disabled={!canWrite}
          onBlur={(e) => e.target.value !== row.label && onSave(row, { label: e.target.value })}
        />
        <TextField
          label="Key"
          size="small"
          defaultValue={row.key}
          disabled={!canWrite}
          helperText="Stable id"
          onBlur={(e) => e.target.value !== row.key && onSave(row, { key: e.target.value })}
          sx={{ minWidth: 180 }}
        />
        <TextField
          select
          label="Parent"
          size="small"
          value={row.parent == null ? '' : String(row.parent)}
          disabled={!canWrite}
          onChange={(e) => onSave(row, { parent: e.target.value === '' ? null : Number(e.target.value) })}
          sx={{ minWidth: 200 }}
          helperText="Nest under another block"
        >
          <MenuItem value="">None (top level)</MenuItem>
          {parentOptions.map((b) => (
            <MenuItem key={b.id} value={String(b.id)}>
              {(b.label || b.key || `#${b.id}`) + ` · ${b.category}`}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        {isAiConfig ? (
          <AiQuestionConfigEditor
            payload={(row.payload ?? {}) as CampaignContentBlockPayload}
            categories={categories}
            canWrite={canWrite}
            onSave={(payload) => onSavePayload(row, payload)}
          />
        ) : CATEGORY_SCHEMAS[row.category] ? (
          // Friendly, schema-driven per-field editor for every category the runner reads.
          <SchemaPayloadFields
            block={row}
            schema={CATEGORY_SCHEMAS[row.category]}
            canWrite={canWrite}
            campaignId={campaignId}
            onSavePayload={(payload) => onSavePayload(row, payload)}
          />
        ) : (
          // Graceful fallback for unknown/ad-hoc categories with no declared schema.
          <JsonPayloadField
            value={(row.payload ?? {}) as CampaignContentBlockPayload}
            disabled={!canWrite}
            onSave={(payload) => onSavePayload(row, payload as CampaignContentBlockPayload)}
          />
        )}
      </Box>
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * First-class, friendly editor for an `ai_question_config` block's payload. The `enabled` toggle is the
 * explicit, off-by-default AI switch: while off, only the predefined question bank is offered. When on,
 * the AI draws `suggest_count` suggestions from the chosen `pool_category`, steered by
 * `refinement_context`. All four values persist into `payload`.
 */
function AiQuestionConfigEditor(props: {
  payload: CampaignContentBlockPayload;
  categories: string[];
  canWrite: boolean;
  onSave: (payload: CampaignContentBlockPayload) => void;
}): JSX.Element {
  const { payload, categories, canWrite, onSave } = props;
  const enabled = payload.enabled === true;

  const patch = (next: Partial<CampaignContentBlockPayload>) => onSave({ ...payload, ...next });

  return (
    <Stack spacing={1.5} sx={{ pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            disabled={!canWrite}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
        }
        label={enabled ? 'AI question suggestion is ON' : 'AI question suggestion is OFF (predefined bank only)'}
      />
      {!enabled && (
        <Typography variant="caption" color="text.secondary">
          Off by default. While off, SEs only see the predefined question bank — no AI-suggested
          questions are generated.
        </Typography>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <TextField
          select
          label="Pool category"
          size="small"
          value={payload.pool_category ?? ''}
          disabled={!canWrite || !enabled}
          onChange={(e) => patch({ pool_category: e.target.value })}
          sx={{ minWidth: 220 }}
          helperText="Which content-block category the AI draws from"
        >
          <MenuItem value="">— select —</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Suggest count"
          size="small"
          type="number"
          value={payload.suggest_count ?? 0}
          disabled={!canWrite || !enabled}
          onChange={(e) => patch({ suggest_count: Number(e.target.value) || 0 })}
          sx={{ width: 150 }}
          helperText="How many to propose"
        />
      </Stack>

      <TextField
        label="Refinement context"
        size="small"
        fullWidth
        multiline
        minRows={2}
        defaultValue={payload.refinement_context ?? ''}
        disabled={!canWrite || !enabled}
        helperText="Guidance that steers the AI's suggestions"
        onBlur={(e) =>
          e.target.value !== (payload.refinement_context ?? '') &&
          patch({ refinement_context: e.target.value })
        }
      />
    </Stack>
  );
}

/** Generic validated JSON editor for a block's category-shaped `payload`. Parses on blur; a bad parse
 *  shows an error and is never saved. */
function JsonPayloadField(props: {
  value: Record<string, unknown>;
  disabled?: boolean;
  onSave: (value: Record<string, unknown>) => void;
}): JSX.Element {
  const { value, disabled, onSave } = props;
  const serialized = React.useMemo(() => JSON.stringify(value ?? {}, null, 2), [value]);
  const [text, setText] = React.useState(serialized);
  const [error, setError] = React.useState<string | null>(null);

  // Reseed when the server value changes (and we're not mid-edit with an error showing).
  React.useEffect(() => {
    setText(serialized);
    setError(null);
  }, [serialized]);

  const commit = () => {
    const trimmed = text.trim();
    if (trimmed === '') {
      setError(null);
      if (Object.keys(value ?? {}).length) onSave({});
      return;
    }
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setError('Payload must be a JSON object.');
        return;
      }
      setError(null);
      if (JSON.stringify(parsed) !== JSON.stringify(value ?? {})) onSave(parsed);
    } catch (e) {
      setError((e as Error)?.message ?? 'Invalid JSON.');
    }
  };

  return (
    <Stack spacing={0.5} sx={{ pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <Typography variant="caption" color="text.secondary">
        Payload (category-shaped JSON)
      </Typography>
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        value={text}
        disabled={disabled}
        error={!!error}
        helperText={error ?? 'Edit the raw JSON; validated on blur.'}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
      />
    </Stack>
  );
}

export default ContentBlocksEditor;
